/* eslint-disable react/require-default-props */
import React, { useContext } from 'react';
import proptypes from 'prop-types';
import { Button } from '@one-thd/sui-atomic-components';
import { QueryProvider, QueryContext } from '@thd-nucleus/data-sources';
import { SwapItem } from '@thd-olt-component-react/swap-item';
import { getBundleDrawerRootProps } from '../../utils/product-bundle-utils';

const { func, string, bool } = proptypes;

/**
 * Props
 *
 * @param {Object} props - The properties object.
 * @param {string} props.itemId - The unique identifier of the item to be swapped.
 * @param {Function} props.swapProducts - Callback function to execute the swap action.
 * @param {Function} [props.closeDrawer] - Optional callback to close the drawer after swapping.
 * @param {boolean} [props.hideCurrentSelectedItem] - Whether to hide the currently selected item in the swap drawer.
 * @param {string} [props.buttonText] - The text to display on the swap button.
 * @param {Function} props.setSelectedItemId - Callback to set the currently selected item ID.
 * @param {string} [props.itemIndex='n/a'] - The index of the item in the bundle.
 * @param {boolean} [props.isMobile=false] - Whether the component is rendered on a mobile device.
 *
 * @returns {React.ReactElement} The rendered SwapButton component.
 */

export const SwapButton = ({
  itemId,
  swapProducts,
  closeDrawer,
  hideCurrentSelectedItem = false,
  buttonText = 'Swap',
  setSelectedItemId,
  itemIndex = 'n/a',
  isMobile = false
}) => {
  const { defaultVariables } = useContext(QueryContext) || {};
  return (
    <QueryProvider
      cacheKey="bundle-products-swap-item"
      defaultVariables={defaultVariables?.current}
    >
      <SwapItem
        itemId={itemId}
        onSwap={(swappedItem) => {
          if (swappedItem) {
            setSelectedItemId(itemId);
            swapProducts(itemId, swappedItem?.product?.itemId);
            if (closeDrawer) closeDrawer();
          }
        }}
        itemIndex={itemIndex}
        closeOnSwap
        openInQuickViewDescription
        openDetailsInNewTab
        hideQuickViewMask
        hideCurrentSelectedItem={hideCurrentSelectedItem}
        drawerRootProps={getBundleDrawerRootProps(isMobile)}
        customElement={(
          <Button
            fullWidth
            variant="secondary"
            data-testid="swap-cta-button"
          >
            {buttonText}
          </Button>
        )}
      />
    </QueryProvider>
  );
};

SwapButton.propTypes = {
  itemId: string,
  swapProducts: func.isRequired,
  closeDrawer: func,
  setSelectedItemId: func.isRequired,
  buttonText: string,
  itemIndex: string,
  isMobile: bool,
  hideCurrentSelectedItem: bool
};